/**
 * Input group primitives for shadcn-style {@link https://ui.shadcn.com/docs/components/combobox Combobox} on Base UI.
 *
 * Vendored because the registry combobox depends on these pieces for a single bordered field
 * with an inline trigger and optional clear control; `pnpm dlx shadcn add combobox` is not
 * published for our style yet. Uses project `Button` and native inputs instead of shadcn v4 paths.
 */
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps, KeyboardEvent } from "react";
import { Button, type ButtonSizeProps } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const focusInputInGroup = (container: HTMLElement) => {
  container.parentElement?.querySelector<HTMLInputElement>("input")?.focus();
};

const InputGroup = ({ className, ...props }: ComponentProps<"div">) => {
  return (
    // biome-ignore lint/a11y/useSemanticElements: shadcn combobox uses div+role=group; fieldset breaks flex/input-group layout
    <div
      data-slot="input-group"
      role="group"
      className={cn(
        "group/input-group relative flex w-full items-center rounded-md border border-slate-200 bg-white shadow-xs outline-none",
        "h-9 min-w-0 has-[>textarea]:h-auto",
        "has-[[data-slot=input-group-control]:focus-visible]:border-blue-600 has-[[data-slot=input-group-control]:focus-visible]:ring-1 has-[[data-slot=input-group-control]:focus-visible]:ring-blue-600",
        "has-[[data-slot][aria-invalid=true]]:border-red-600 has-[[data-slot][aria-invalid=true]]:ring-1 has-[[data-slot][aria-invalid=true]]:ring-red-600",
        "has-[>[data-align=inline-start]]:[&>input]:pl-2",
        "has-[>[data-align=inline-end]]:[&>input]:pr-2",
        className,
      )}
      {...props}
    />
  );
};

const inputGroupAddonVariants = cva(
  "flex h-auto cursor-text select-none items-center justify-center gap-2 py-1.5 text-sm font-medium text-slate-500 group-data-[disabled=true]/input-group:opacity-50 [&>svg:not([class*='size-'])]:size-4",
  {
    variants: {
      align: {
        "inline-start": "order-first pl-3 has-[>button]:ml-[-0.45rem]",
        "inline-end": "order-last pr-3 has-[>button]:mr-[-0.45rem]",
        "block-start":
          "order-first w-full justify-start px-3 pt-3 group-has-[>input]/input-group:pt-2.5 [.border-b]:pb-3",
        "block-end":
          "order-last w-full justify-start px-3 pb-3 group-has-[>input]/input-group:pb-2.5 [.border-t]:pt-3",
      },
    },
    defaultVariants: {
      align: "inline-start",
    },
  },
);

const InputGroupAddon = ({
  className,
  align = "inline-start",
  onClick,
  onKeyDown,
  ...props
}: ComponentProps<"div"> & VariantProps<typeof inputGroupAddonVariants>) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    if ((event.target as HTMLElement).closest("button")) return;
    event.preventDefault();
    focusInputInGroup(event.currentTarget);
  };

  return (
    // biome-ignore lint/a11y/useSemanticElements: addon is layout chrome; focus moves to the real input
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        if ((event.target as HTMLElement).closest("button")) return;
        focusInputInGroup(event.currentTarget);
      }}
      onKeyDown={handleKeyDown}
      {...props}
    />
  );
};

const inputGroupButtonVariants = cva(
  "flex items-center gap-2 text-sm shadow-none",
  {
    variants: {
      inputGroupSize: {
        xs: "h-6 gap-1 rounded-md px-2 has-[>svg]:px-2 [&>svg:not([class*='size-'])]:size-3.5",
        sm: "h-8 gap-1.5 rounded-md px-2.5 has-[>svg]:px-2.5",
        "icon-xs": "size-6 rounded-md p-0 has-[>svg]:p-0",
        "icon-sm": "size-8 p-0 has-[>svg]:p-0",
      },
    },
    defaultVariants: {
      inputGroupSize: "xs",
    },
  },
);

type InputGroupButtonSize = NonNullable<
  VariantProps<typeof inputGroupButtonVariants>["inputGroupSize"]
>;

const inputGroupSizeToButtonSize = (
  inputGroupSize: InputGroupButtonSize,
): ButtonSizeProps => {
  if (inputGroupSize === "icon-xs") return "xsIcon";
  if (inputGroupSize === "icon-sm") return "icon";
  if (inputGroupSize === "sm") return "sm";
  return "sm";
};

interface InputGroupButtonProps extends ComponentProps<typeof Button> {
  inputGroupSize?: InputGroupButtonSize;
}

const InputGroupButton = ({
  className,
  type = "button",
  variant = "ghost",
  inputGroupSize = "xs",
  size: buttonSizeProp,
  ...props
}: InputGroupButtonProps) => {
  const buttonSize =
    buttonSizeProp ?? inputGroupSizeToButtonSize(inputGroupSize);

  return (
    <Button
      type={type}
      data-size={inputGroupSize}
      variant={variant}
      size={buttonSize}
      className={cn(inputGroupButtonVariants({ inputGroupSize }), className)}
      {...props}
    />
  );
};

const InputGroupInput = ({ className, ...props }: ComponentProps<"input">) => {
  return (
    <input
      data-slot="input-group-control"
      className={cn(
        "flex-1 rounded-none border-0 bg-transparent px-3 py-2 text-xs text-gray-900 shadow-none outline-hidden placeholder:text-gray-500 focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  );
};

export { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput };
